import { isEmpty } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { isNullOrUndefined, isString } from 'util';

const emailRex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export type ValidateFunction = (value?: any) => boolean | string | null;
export type FieldValidators = { [name: string]: ValidateFunction[] };
export type ValidationErrors = { [name: string]: string };

export const Validators = {
  required: (value?: string) => !isEmpty(value),
  emailFormat: (value?: string) =>
    !isNullOrUndefined(value) && emailRex.test(value),
  futureDate: (timeFormat: string) => (value?: string) => {
    if (
      isNullOrUndefined(value) ||
      isEmpty(value) ||
      moment(value, timeFormat).isAfter(moment())
    ) {
      return null;
    }
    return 'futureDate';
  }
};

function _validate(
  validators: ValidateFunction[],
  value?: string
): string | null {
  for (let i = 0; i < validators.length; i++) {
    let validateFunc = validators[i];
    let res = validateFunc(value);
    // console.log(`Validate ${validateFunc.name} for value ${value}: ${res}`);
    if (res === false) {
      return validateFunc.name;
    } else if (isString(res) && !isEmpty(res)) {
      return res;
    }
  }
  return null;
}

export function useValidators(
  initialValues: { [name: string]: any },
  fieldValidators: FieldValidators,
  onErrorsChange?: (errors: ValidationErrors) => void
): [
  ValidationErrors,
  (name: string, value?: any) => void,
  (obj: { [name: string]: any }, properties: string[]) => void
] {
  const raiseErrorsChange = (errors: ValidationErrors) => {
    // Notify errors to outside world
    if (!isNullOrUndefined(onErrorsChange)) {
      onErrorsChange(errors);
    }
  };

  const [errors, setErrors] = useState(() => {
    // Initial error checking based on given content
    const errors: ValidationErrors = {};
    for (let key in fieldValidators) {
      const fvs = fieldValidators[key];
      const value = initialValues[key];
      const res = _validate(fvs, value);
      if (!isNullOrUndefined(res)) {
        errors[key] = res;
      }
    }
    raiseErrorsChange(errors);
    return errors;
  });

  // Validate a single field
  const validate = (name: string, value?: string) => {
    // Try to get the list of field validators for the tested field
    const fvs = fieldValidators[name];
    if (isEmpty(fvs)) {
      return;
    }
    const newErrors: ValidationErrors = { ...errors };
    const res = _validate(fvs, value);
    if (isNullOrUndefined(res)) {
      delete newErrors[name];
    } else {
      newErrors[name] = res;
    }

    setErrors(newErrors);
    raiseErrorsChange(newErrors);
  };

  const validateObject = (
    obj: { [name: string]: any },
    properties: string[]
  ) => {
    const newErrors: ValidationErrors = { ...errors };
    properties.forEach(name => {
      const fvs = fieldValidators[name];
      if (isEmpty(fvs)) {
        return;
      }
      const res = _validate(fvs, obj[name]);
      if (isNullOrUndefined(res)) {
        delete newErrors[name];
      } else {
        newErrors[name] = res;
      }
    });

    setErrors(newErrors);
    raiseErrorsChange(newErrors);
  };

  return [errors, validate, validateObject];
}
